import { createContext, useContext, useMemo, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { getTenantIdFromLS, getTenantSkFromLS } from 'utils/localStorageUtils';

import { WebSocketConnectionContext } from 'context/WebSocketConnectionContext';
import { useToast } from 'context/ToastContext';
import { UserDetailsContext, useUserDetailsContext } from 'context/userDetailsContext';

import { toastConstant } from 'constants/toastmessage';

import { getClientUserList } from 'services/users/userServices';
import { getMessages } from 'services/messages/messageService';
import { useNavbarContext } from 'context/NavbarContext';
import { updateMessageThread } from 'services/messageServices';
import { convertCaseToFilterFormat, formatContactList } from 'utils/utils';
import { handleFilterResetUserContext } from 'common/pagination';
import useAutomatedTime from 'hooks/useAutomatedTime';
import { convertToTimezoneFormat } from 'utils/utility_functions/timezone';
import moment from 'moment';
import { totalCountEvent } from 'services/generalServices';
import { listUsers } from 'services/users/userServices';

// secure message context
const SecureMessageContext = createContext({});

export const useSecureMessageContext = () => useContext(SecureMessageContext);

export const SecureMessageContextProvider = (props) => {
  const {
    isLeadSpecific,
    caseDetails,
    isLeadClosed,
    isGlobalMessagesScreen,
    isFromClientPortal,
    isFromCaseSpecificModule,
    clientPortalDashboard,
    clientDashboardSelectedCase,
    getCaseDetails,
  } = props;
  let { id } = useParams();
  const { userDetails } = useUserDetailsContext();
  const { onMountUnMount, onOperationBreak } = useAutomatedTime(caseDetails?.case_id, caseDetails?.case_description?.case_name);

  function getPageLimit() {
    if (isLeadSpecific && userContext) {
      return userContext?.firmDetails?.pagination_preference?.leads?.message;
    } else if (isFromCaseSpecificModule && userContext) {
      return userContext?.firmDetails?.pagination_preference?.case?.message;
    } else if (isGlobalMessagesScreen && userContext) {
      return userContext?.firmDetails?.pagination_preference?.messages?.self;
    }
  }

  const websocketContext = useContext(WebSocketConnectionContext);

  const userContext = useContext(UserDetailsContext);
  const { getNotificationCounts, notificationData } = useNavbarContext();

  const { addToast } = useToast();

  const [caseMessages, setCaseMessages] = useState([]);
  const [isCaseClosed, setIsCaseClosed] = useState(false);
  const [authorsList, setAuthorsList] = useState([]);
  const [clientsList, setClientsList] = useState([]);

  const [searchKeyWord, setSearchKeyWord] = useState('');
  const [filterCaseId, setFilterCaseId] = useState(
    isFromClientPortal
      ? convertCaseToFilterFormat(userContext?.clientSelectedCase)
      : isGlobalMessagesScreen
      ? userDetails?.user_preferences?.filters?.secure_messages?.case || ''
      : ''
  );
  const [filterMsgStatus, setFilterMsgStatus] = useState(
    isGlobalMessagesScreen
      ? userDetails?.user_preferences?.filters?.secure_messages?.message_status === 'All'
        ? ''
        : userDetails?.user_preferences?.filters?.secure_messages?.message_status
      : ''
  );
  const [filterAuthorId, setFilterAuthorId] = useState(
    isGlobalMessagesScreen ? userDetails?.user_preferences?.filters?.secure_messages?.author : []
  );
  const [dateForFilter, setDateForFilter] = useState(() => {
    if (isGlobalMessagesScreen) {
      const dateArr = userDetails?.user_preferences?.filters?.secure_messages?.date;
      if (Array.isArray(dateArr) && dateArr.length === 2) {
        return dateArr.map((d) => (d ? new Date(d) : null));
      }
    }
    return [];
  });
  const [filterSentiment, setFilterSentiment] = useState(
    isGlobalMessagesScreen ? userDetails?.user_preferences?.filters?.secure_messages?.sentiment : ''
  );
  const [filterParticipant, setFilterParticipant] = useState(
    isGlobalMessagesScreen ? userDetails?.user_preferences?.filters?.secure_messages?.participants : ''
  );

  const [next_Token, setNext_Token] = useState(null);
  const [transition_Token, setTransition_Token] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [isAuthorLoading, setIsAuthorLoading] = useState(false);
  const [firmUserList, setFirmUserList] = useState([]);
  const [messageScheduleData, setMessageScheduleData] = useState(null);

  useEffect(() => {
    if (!clientPortalDashboard) {
      // fetchCaseMessage({});
      let obj = {
        caseId: isGlobalMessagesScreen ? '' : caseDetails?.case_id,
        is_contact_list: false,
        is_team_member_list: false,
        all_contacts: false,
        contact_id: false,
        is_lead: false,
        is_mandatory_lawft_access: true,
        is_active: false,
        excludeFromLoading: true,
        is_include_groups: true,
        active_groups: true,
      };
      getAuthorsList(obj);
    }
  }, []);

  useEffect(() => {
    if (isFromCaseSpecificModule && caseDetails?.sk && caseDetails?.pk) {
      if (caseDetails?.status === 'INACTIVE') {
        setIsCaseClosed(true);
      } else if (caseDetails?.status === 'ACTIVE') {
        setIsCaseClosed(false);
      }
    }
  }, [caseDetails]);

  useEffect(() => {
    if (websocketContext.response) {
      if (websocketContext.response.secure_message_type === 'thread' && websocketContext.response.body) {
        websocketContext.setResponse();
      }
    }
    if (websocketContext.secureMessageData) {
      if (websocketContext.secureMessageData.secure_message_type === 'thread' && websocketContext.secureMessageData.body) {
        websocketContext.setSecureMessageData();
      }
    }
    if (websocketContext.secureMessageData) {
      if (websocketContext.secureMessageData.secure_message_type === 'reply' && websocketContext.secureMessageData.body) {
        websocketContext.setSecureMessageData();
      }
    }
    if (!clientPortalDashboard) {
      // fetchCaseMessage({});
    }
  }, [websocketContext.response, websocketContext.secureMessageData]);

  useEffect(() => {
    if (websocketContext.secureMessageError) {
      if (messageScheduleData) {
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.MESSAGE_SCHEDULED_FAILED);
      } else {
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.MESSAGE_SEND_FAILURE);
      }
      websocketContext.setSecureMessageError();
      setMessageScheduleData(null);
    }
  }, [websocketContext.secureMessageError]);

  // fetch message list
  const fetchCaseMessage = (obj) => {
    const tenantId = getTenantIdFromLS();
    const tenant_sk = getTenantSkFromLS();
    let tenantSk = tenant_sk ? tenant_sk.replaceAll('#', '%23') : '';

    let idKey = isLeadSpecific ? caseDetails.case_id : id;

    obj.case_id = isGlobalMessagesScreen
      ? obj?.case_id?.id
        ? obj?.case_id?.id
        : obj?.case_id !== undefined
        ? obj?.case_id
        : obj?.filterCaseId !== null
        ? filterCaseId?.id
        : ''
      : idKey;
    obj.is_global = isGlobalMessagesScreen ? true : false;
    obj.pageLimit = obj.pageLimit ?? getPageLimit();
    obj.tenantId = tenantId;
    obj.tenantSk = tenantSk;
    obj.is_read = obj?.is_read !== null && obj?.is_read !== undefined ? obj.is_read : '';
    obj.scheduled_status = obj?.scheduled_status !== null && obj?.scheduled_status !== undefined ? obj.scheduled_status : '';
    obj.author_id = obj?.author_id ? obj?.author_id : '';
    getMessages(obj)
      .then((response) => {
        setCaseMessages(response.data);
        setNext_Token(response.data.next_token);
        setTransition_Token(response.data.transition_token);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  function getAuthorsList(obj) {
    setIsAuthorLoading(true);
    getClientUserList(obj)
      .then((res) => {
        const formattedList = formatContactList(res?.data);
        const sortedList = formattedList?.map((item) => {
          if (item?.code === 'Users') {
            return {
              ...item,
              items: item.items.sort((a, b) => {
                const nameA = (a?.first_name || a?.assignee_name || '').toLowerCase();
                const nameB = (b?.first_name || b?.assignee_name || '').toLowerCase();
                return nameA.localeCompare(nameB);
              }),
            };
          } else if (item?.code === 'Groups') {
            return {
              ...item,
              items: item.items.sort((a, b) => (a.group_name?.toLowerCase() || '').localeCompare(b.group_name?.toLowerCase() || '')),
            };
          }
          return item;
        });

        setAuthorsList(sortedList);
        setClientsList(res.data);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => setIsAuthorLoading(false));
  }

  const refreshCase = () => {
    setSearchKeyWord('');
    setFilterAuthorId('');
    setDateForFilter('');
    setFilterMsgStatus('');
    setFilterSentiment('');
    setFilterParticipant('');

    const updatedFilterCaseId = isFromClientPortal ? convertCaseToFilterFormat(userContext?.clientSelectedCase) : '';
    setFilterCaseId(updatedFilterCaseId);
    let obj = {
      caseId: isFromClientPortal ? updatedFilterCaseId?.id : '',
      is_global: isGlobalMessagesScreen,
      pageLimit: getPageLimit(),
    };

    obj.case_id = isFromClientPortal ? updatedFilterCaseId?.id : '';
    const resetFilterObj = {
      date: null,
      message_status: null,
      sentiment: null,
      case: null,
      author: null,
      participants: null,
    };

    // Create a temporary object with cleared filterCaseId for this specific call
    const tempObj = { ...obj };
    if (!isFromClientPortal) {
      tempObj.filterCaseId = null; // This will prevent fallback to old filterCaseId
    }

    fetchCaseMessage(tempObj);
    handleFilterResetUserContext(userContext, 'filters', 'secure_messages', null, resetFilterObj, false);
  };

  const handleMessageStatus = (e, val, isRead, id) => {
    e.preventDefault();

    let obj = {
      case_id: filterCaseId,
      author_id: filterAuthorId,
      is_read: filterMsgStatus === 'scheduled' ? '' : filterMsgStatus,
      scheduled_status: filterMsgStatus === 'scheduled' ? filterMsgStatus : '',
      keyword: searchKeyWord,
      participant: filterParticipant,
      sentiment_filter: filterSentiment,
      ...pickExtractedDate(dateForFilter),
    };
    try {
      if (val && val.sk) {
        const tenantId = getTenantIdFromLS();
        const tenant_sk = getTenantSkFromLS();
        let tenantSk = tenant_sk ? tenant_sk.replaceAll('#', '%23') : '';
        let idKey = isLeadSpecific ? caseDetails.case_id : id;
        let sendData = {};
        sendData.sk = val.sk;
        sendData.is_new = false;
        sendData.case_id = idKey ? idKey : val?.case_id;
        sendData.is_read = !isRead;
        sendData.old_is_new = val?.is_new;
        sendData.case_name = val?.case_name;
        sendData.subject = val?.subject;
        sendData.accessible_list = val?.accessible_list;
        sendData.assign_to = val?.assign_to || [];
        sendData.resolution_status = val?.resolution_status || false;
        sendData.resolved_status = val?.resolved_status || false;

        // updateMessageThread(tenantId, tenantSk, sendData, false)
        return updateMessageThread(tenantId, tenantSk, sendData, isLeadSpecific ? true : false)
          .then((response) => {
            //Fetching notification counts
            !isFromClientPortal && notificationData?.loadNotificationCount();
            isFromClientPortal && getNotificationCounts(userContext?.firmDetails?.contact_id);
            // Call fetchCaseMessage only after updateMessageThread is completed and if there is response
            if (response?.data) {
              fetchCaseMessage(obj);
            }
            // refreshCase();
          })
          .catch((error) => {
            console.log(error);
            fetchCaseMessage(obj);
            // refreshCase();
          });
      }
    } catch (error) {
      // refreshCase();
    }
    return Promise.resolve();
  };

  const pickExtractedDate = (dateArray) => {
    return {
      message_date: dateArray?.[0] ? convertToTimezoneFormat(moment(dateArray[0]).startOf('day'), userDetails.timezone) : '',
      to_limit: dateArray?.[0]
        ? convertToTimezoneFormat(moment(dateArray[1] ? dateArray[1] : dateArray[0]).endOf('day'), userDetails.timezone)
        : '',
    };
  };

  const loadFirmUserList = () => {
    totalCountEvent('user').then((response) => {
      listUsers({ is_include_groups: false, is_confirmed_user: true, status: 'ACTIVE', limit: response.data, active_groups: true })
        .then((response) => setFirmUserList(response.data?.firm_users || []))
        .catch((error) => console.log(error));
    });
  };

  const contextPayload = useMemo(
    () => ({
      fetchCaseMessage,
      caseMessages,
      caseDetails,
      isCaseClosed,
      isLeadClosed,
      isLeadSpecific,
      isGlobalMessagesScreen,
      isFromClientPortal,
      isFromCaseSpecificModule,
      next_Token,
      transition_Token,
      authorsList,
      clientsList,
      clientPortalDashboard,
      clientDashboardSelectedCase,
      filterCaseId,
      onMountUnMount,
      onOperationBreak,
      setFilterCaseId,
      filterMsgStatus,
      setFilterMsgStatus,
      filterAuthorId,
      setFilterAuthorId,
      dateForFilter,
      setDateForFilter,
      searchKeyWord,
      setSearchKeyWord,
      refreshCase,
      searchValue,
      setSearchValue,
      handleMessageStatus,
      filterSentiment,
      setFilterSentiment,
      filterParticipant,
      setFilterParticipant,
      isAuthorLoading,
      loadFirmUserList,
      firmUserList,
      messageScheduleData,
      setMessageScheduleData,
      getCaseDetails,
    }),
    [
      caseMessages,
      caseDetails,
      isCaseClosed,
      isLeadClosed,
      isLeadSpecific,
      isGlobalMessagesScreen,
      isFromClientPortal,
      isFromCaseSpecificModule,
      authorsList,
      clientsList,
      clientPortalDashboard,
      clientDashboardSelectedCase,
      next_Token,
      transition_Token,
      filterCaseId,
      filterMsgStatus,
      filterAuthorId,
      dateForFilter,
      searchKeyWord,
      userContext,
      onMountUnMount,
      onOperationBreak,
      searchValue,
      setSearchValue,
      handleMessageStatus,
      filterSentiment,
      filterParticipant,
      isAuthorLoading,
      loadFirmUserList,
      firmUserList,
      messageScheduleData,
      setMessageScheduleData,
      getCaseDetails,
    ]
  );

  return <SecureMessageContext.Provider value={contextPayload} {...props} />;
};
